// Jarvis Voice Hook
// Realtime API (VAD + 音声応答) + Whisper API (STTテキスト表示)

import { useState, useRef, useCallback, useEffect } from 'react';
import { tokenStorage } from '../lib/api';

const API_BASE = import.meta.env.DEV 
  ? 'http://localhost:8787' 
  : 'https://reactor-jarvis-app.valleyintest.workers.dev';

interface VoiceSession {
  sessionId: string;
  token: string;
  expiresAt: number;
  endpoint: string;
  model: string;
}

interface VoiceEvent {
  type: string;
  [key: string]: unknown;
}

export interface ChatMessage {
  id: string;
  role: 'jarvis' | 'user';
  content: string;
  timestamp: Date;
  pending?: boolean; // STT処理中
}

export interface UseRealtimeAudioReturn {
  isConnected: boolean;
  isListening: boolean;
  isSpeaking: boolean;
  isMuted: boolean;
  error: string | null;
  messages: ChatMessage[];
  isComplete: boolean;
  connect: () => Promise<void>;
  disconnect: () => void;
  toggleListening: () => void;
  setMuted: (muted: boolean) => void;
  clearMessages: () => void;
}

export function useRealtimeAudio(): UseRealtimeAudioReturn {
  const [isConnected, setIsConnected] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isComplete, setIsComplete] = useState(false);
  
  const wsRef = useRef<WebSocket | null>(null);
  const playbackContextRef = useRef<AudioContext | null>(null);
  const captureContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const audioQueueRef = useRef<AudioBuffer[]>([]);
  const isPlayingRef = useRef(false);
  const isMutedRef = useRef(false);
  const currentResponseRef = useRef<string>('');
  const hasGreetedRef = useRef(false);
  
  // STT用: 録音チャンクを保持
  const recordingChunksRef = useRef<Int16Array[]>([]);
  const lastSpeechTimeRef = useRef<number>(0);
  const sttPendingRef = useRef<boolean>(false);

  // 音声セッションを作成
  const createSession = useCallback(async (): Promise<VoiceSession> => {
    const authToken = tokenStorage.get();
    const res = await fetch(`${API_BASE}/api/voice/session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
      },
      body: JSON.stringify({ voice: 'sage' }),
    });
    
    if (!res.ok) {
      throw new Error('音声セッションの作成に失敗しました');
    }
    
    return res.json();
  }, []);

  // メッセージを追加
  const addMessage = useCallback((role: 'jarvis' | 'user', content: string, pending = false) => {
    const newMessage: ChatMessage = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      role,
      content,
      timestamp: new Date(),
      pending,
    };
    setMessages(prev => [...prev, newMessage]);
    
    // 設計書作成のトリガーをチェック
    if (role === 'jarvis' && content.includes('設計書を作成')) {
      setIsComplete(true);
    }
    
    return newMessage.id;
  }, []);

  // メッセージを更新
  const updateMessage = useCallback((id: string, content: string, pending = false) => {
    setMessages(prev => prev.map(m => 
      m.id === id ? { ...m, content, pending } : m
    ));
  }, []);

  // 再生用AudioContextを初期化
  const getPlaybackContext = useCallback(() => {
    if (!playbackContextRef.current || playbackContextRef.current.state === 'closed') {
      playbackContextRef.current = new AudioContext({ sampleRate: 24000 });
    }
    return playbackContextRef.current;
  }, []);

  // キューから次の音声を再生
  const playNextInQueue = useCallback(() => {
    const buffer = audioQueueRef.current.shift();
    if (!buffer) {
      isPlayingRef.current = false;
      return;
    }
    
    isPlayingRef.current = true;
    const ctx = getPlaybackContext();
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.onended = () => {
      playNextInQueue();
    };
    source.start();
  }, [getPlaybackContext]);

  // 音声データを再生
  const playAudioDelta = useCallback((base64Audio: string) => {
    if (isMutedRef.current) return;
    
    try {
      const ctx = getPlaybackContext();
      
      const audioData = atob(base64Audio);
      const arrayBuffer = new ArrayBuffer(audioData.length);
      const view = new Uint8Array(arrayBuffer);
      for (let i = 0; i < audioData.length; i++) {
        view[i] = audioData.charCodeAt(i);
      }
      
      const int16Array = new Int16Array(arrayBuffer);
      const float32Array = new Float32Array(int16Array.length);
      for (let i = 0; i < int16Array.length; i++) {
        float32Array[i] = int16Array[i] / 32768;
      }
      
      const audioBuffer = ctx.createBuffer(1, float32Array.length, 24000);
      audioBuffer.copyToChannel(float32Array, 0);
      
      audioQueueRef.current.push(audioBuffer);
      
      if (!isPlayingRef.current) {
        playNextInQueue();
      }
    } catch (err) {
      console.error('Audio playback error:', err);
    }
  }, [getPlaybackContext, playNextInQueue]);

  // STT: 録音した音声をWhisper APIでテキスト化（バックグラウンド）
  const transcribeRecordedAudio = useCallback(async () => {
    if (recordingChunksRef.current.length === 0 || sttPendingRef.current) return;
    
    sttPendingRef.current = true;
    
    // チャンクを結合
    const totalLength = recordingChunksRef.current.reduce((acc, chunk) => acc + chunk.length, 0);
    const combinedAudio = new Int16Array(totalLength);
    let offset = 0;
    for (const chunk of recordingChunksRef.current) {
      combinedAudio.set(chunk, offset);
      offset += chunk.length;
    }
    recordingChunksRef.current = [];
    
    // WAVに変換
    const wavBuffer = int16ToWav(combinedAudio, 24000);
    const audioBlob = new Blob([wavBuffer], { type: 'audio/wav' });
    
    // プレースホルダーメッセージを追加
    const msgId = addMessage('user', '...', true);
    
    try {
      const authToken = tokenStorage.get();
      const formData = new FormData();
      formData.append('audio', audioBlob, 'audio.wav');

      const res = await fetch(`${API_BASE}/api/voice/transcribe`, {
        method: 'POST',
        headers: {
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
        },
        body: formData,
      });

      if (res.ok) {
        const result = await res.json();
        if (result.transcript) {
          updateMessage(msgId, result.transcript, false);
        } else {
          // 認識できなかった場合はメッセージを削除
          setMessages(prev => prev.filter(m => m.id !== msgId));
        }
      } else {
        setMessages(prev => prev.filter(m => m.id !== msgId));
      }
    } catch (err) {
      console.error('STT error:', err);
      setMessages(prev => prev.filter(m => m.id !== msgId));
    } finally {
      sttPendingRef.current = false;
    }
  }, [addMessage, updateMessage]);

  // Int16ArrayをWAVに変換
  const int16ToWav = (samples: Int16Array, sampleRate: number): ArrayBuffer => {
    const buffer = new ArrayBuffer(44 + samples.length * 2);
    const view = new DataView(buffer);
    
    // WAVヘッダー
    const writeString = (offset: number, str: string) => {
      for (let i = 0; i < str.length; i++) {
        view.setUint8(offset + i, str.charCodeAt(i));
      }
    };
    
    writeString(0, 'RIFF');
    view.setUint32(4, 36 + samples.length * 2, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeString(36, 'data');
    view.setUint32(40, samples.length * 2, true);
    
    // 音声データ
    for (let i = 0; i < samples.length; i++) {
      view.setInt16(44 + i * 2, samples[i], true);
    }
    
    return buffer;
  };

  // 音声イベント処理
  const handleRealtimeEvent = useCallback((event: VoiceEvent) => {
    switch (event.type) {
      case 'session.created':
      case 'session.updated':
        // セッション準備完了、挨拶を開始
        if (!hasGreetedRef.current && wsRef.current) {
          hasGreetedRef.current = true;
          setTimeout(() => {
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({
                type: 'conversation.item.create',
                item: {
                  type: 'message',
                  role: 'user',
                  content: [{ type: 'input_text', text: 'セッションが開始しました。ユーザーに日本語で挨拶し、自己紹介した後、どのようなシステムを作りたいか聞いてください。簡潔に話してください。' }],
                },
              }));
              wsRef.current.send(JSON.stringify({ type: 'response.create' }));
            }
          }, 500);
        }
        break;

      case 'input_audio_buffer.speech_stopped':
        // ユーザーが話し終わった - STTをトリガー
        lastSpeechTimeRef.current = Date.now();
        setTimeout(() => {
          transcribeRecordedAudio();
        }, 100);
        break;

      case 'response.audio_transcript.delta':
        currentResponseRef.current += (event.delta as string);
        break;
      
      case 'response.audio_transcript.done':
        if (currentResponseRef.current) {
          addMessage('jarvis', currentResponseRef.current);
          currentResponseRef.current = '';
        }
        setIsSpeaking(false);
        break;
      
      case 'response.audio.delta':
        setIsSpeaking(true);
        if (event.delta) {
          playAudioDelta(event.delta as string);
        }
        break;
      
      case 'response.done':
        setIsSpeaking(false);
        break;
      
      case 'error':
        console.error('Realtime API error:', event);
        setError((event.error as { message?: string })?.message || 'Unknown error');
        break;
    }
  }, [addMessage, playAudioDelta, transcribeRecordedAudio]);

  // WebSocket接続
  const connect = useCallback(async () => {
    try {
      setError(null);
      hasGreetedRef.current = false;
      
      const session = await createSession();
      
      const ws = new WebSocket(
        `${session.endpoint}?model=${session.model}`,
        ['realtime', `openai-insecure-api-key.${session.token}`, 'openai-beta.realtime-v1']
      );
      
      ws.onopen = () => {
        setIsConnected(true);
        console.log('Jarvis voice session connected');
      };
      
      ws.onmessage = (event) => {
        const data: VoiceEvent = JSON.parse(event.data);
        handleRealtimeEvent(data);
      };
      
      ws.onerror = (e) => {
        console.error('WebSocket error:', e);
        setError('音声接続エラーが発生しました');
        setIsConnected(false);
      };
      
      ws.onclose = () => {
        setIsConnected(false);
        setIsListening(false);
      };
      
      wsRef.current = ws;
    } catch (err) {
      console.error('Connection error:', err);
      setError(err instanceof Error ? err.message : '接続に失敗しました');
    }
  }, [createSession, handleRealtimeEvent]);

  // マイク録音開始
  const startListening = useCallback(async () => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      setError('接続されていません');
      return;
    }
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          sampleRate: 24000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        }
      });
      mediaStreamRef.current = stream;
      
      captureContextRef.current = new AudioContext({ sampleRate: 24000 });
      const source = captureContextRef.current.createMediaStreamSource(stream);
      const processor = captureContextRef.current.createScriptProcessor(4096, 1, 1);
      
      // ミュート用のGainNode
      const muteGain = captureContextRef.current.createGain();
      muteGain.gain.value = 0;
      
      processor.onaudioprocess = (e) => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
        
        const inputData = e.inputBuffer.getChannelData(0);
        const int16Array = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          int16Array[i] = Math.max(-32768, Math.min(32767, inputData[i] * 32768));
        }
        
        // STT用に保存
        recordingChunksRef.current.push(new Int16Array(int16Array));
        
        // Realtime APIに送信
        const base64Audio = btoa(String.fromCharCode(...new Uint8Array(int16Array.buffer)));
        wsRef.current.send(JSON.stringify({
          type: 'input_audio_buffer.append',
          audio: base64Audio,
        }));
      };
      
      source.connect(processor);
      processor.connect(muteGain);
      muteGain.connect(captureContextRef.current.destination);
      processorRef.current = processor;
      
      setIsListening(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'マイクへのアクセスが拒否されました');
    }
  }, []);

  // マイク録音停止
  const stopListening = useCallback(() => {
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
    
    if (captureContextRef.current) {
      captureContextRef.current.close();
      captureContextRef.current = null;
    }
    
    // 残りのSTTを処理
    if (recordingChunksRef.current.length > 0) {
      transcribeRecordedAudio();
    }
    
    setIsListening(false);
  }, [transcribeRecordedAudio]);

  // マイクのトグル
  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  // 切断
  const disconnect = useCallback(() => {
    stopListening();
    
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    if (playbackContextRef.current) {
      playbackContextRef.current.close();
      playbackContextRef.current = null;
    }
    
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    hasGreetedRef.current = false;
    recordingChunksRef.current = [];
    
    setIsConnected(false);
    setIsListening(false);
    setIsSpeaking(false);
  }, [stopListening]);

  // ミュート設定
  const handleSetMuted = useCallback((muted: boolean) => {
    setIsMuted(muted);
    isMutedRef.current = muted;
    
    if (muted) {
      audioQueueRef.current = [];
      isPlayingRef.current = false;
    }
  }, []);

  // メッセージクリア
  const clearMessages = useCallback(() => {
    setMessages([]);
    setIsComplete(false);
    hasGreetedRef.current = false;
    recordingChunksRef.current = [];
  }, []);

  // クリーンアップ
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    isConnected,
    isMuted,
    isListening,
    isSpeaking,
    error,
    messages,
    isComplete,
    connect,
    disconnect,
    toggleListening,
    setMuted: handleSetMuted,
    clearMessages,
  };
}

export type RealtimeVoice = 'sage';
